import * as React from 'react'

import { useSending } from '@modules/hooks/useSending'
import { SvgLogo } from '@modules/icons/code/Logo'
import { Button } from '@modules/shadcn/ui/Button/Button'
import { Card, CardContent, CardFooter, CardHeader } from '@modules/shadcn/ui/Card/Card'

interface Props {
  /** 失敗の理由としてユーザーに見せる文言 */
  message: string
  /** スタッフ情報の取得をやり直す */
  onRetry: () => Promise<void>
  /** サインアウトしてログイン画面へ戻る */
  onSignOut: () => Promise<void>
}

/**
 * サインインは通ったが、スタッフ情報の取得（GetStaffMe）が一時的な失敗で終わったときに出す画面。
 *
 * この画面が無いと、通信断やサーバ内部エラーのときに `staff` がnullのまま何も描画されず、
 * 「ログインしたのに画面が出ない」状態になる（2026-09の事象の一因）。
 * 確定的な拒否（停止済みスタッフ・契約なし企業）はこの画面ではなく、サインアウトして
 * ログイン画面のバナーで理由を出す（@company/auth/staffMeFailure.ts）。
 */
export const AuthRetryScreen: React.FC<Props> = ({ message, onRetry, onSignOut }) => {
  const { sending: retrying, onSubmit: retry } = useSending(onRetry)
  const { sending: signingOut, onSubmit: signOut } = useSending(onSignOut)
  const busy = retrying || signingOut

  return (
    <div className="flex min-h-screen w-full items-center justify-center p-4">
      <div className="w-[340px] sm:w-[400px] max-w-full">
        <div className="mb-10 text-center">
          <SvgLogo viewBox="0 0 104 30" />
        </div>
        <Card>
          <CardHeader>
            <h1 className="text-center font-bold">ログイン情報を取得できませんでした</h1>
          </CardHeader>
          <CardContent>
            <p className="text-sm" role="alert">
              {message}
            </p>
          </CardContent>
          <CardFooter>
            <div className="w-full">
              <Button className="block w-full" disabled={busy} onClick={() => void retry()}>
                {retrying ? '再試行中…' : '再試行する'}
              </Button>
              <Button
                className="mt-2 block w-full"
                disabled={busy}
                onClick={() => void signOut()}
                variant="outline"
              >
                ログイン画面に戻る
              </Button>
            </div>
          </CardFooter>
        </Card>
      </div>
    </div>
  )
}
